import { zodResolver } from '@hookform/resolvers/zod';
import React, { FC, useContext } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { z } from 'zod';

import Button from '@ui/atoms/Button';
import Eyebrow from '@ui/atoms/Eyebrow';
import FlexColumn from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import WYSIWYGContent from '@ui/atoms/WYSIWYGContent';

import SpacedGridItem from '@ui/molecules/SpacedGridItem';
import SubmitButton from '@ui/molecules/SubmitButton';

import FlowContext from '@providers/FlowContextProvider';

import { IFlowStepComponentProps } from '@folx-types/flow-steps';

import theme from '@theme/FolxTheme';

import { dataConstants, schema } from './SsnPromptStep.data';

export interface ISsnPromptStepPageContent {
	pageHeader: string;
	pageDescription: string;
	yesAnswerLabel: string;
	noAnswerLabel: string;
}

interface ISsnPromptStepProps extends IFlowStepComponentProps {
	pageContent: ISsnPromptStepPageContent;
}

const SpacedForm = styled.form`
	margin-top: ${theme.spacing.lg};
`;

const RestyledButton = styled(Button)`
	background: ${theme.colors.white};
	border-radius: 0;
	justify-content: flex-start;

	${Eyebrow} {
		padding: ${theme.spacing.lg} ${theme.spacing.md};
		color: ${theme.colors.black};
	}
`;

const RestyledSubmit = styled(SubmitButton)`
	background: ${theme.colors.white};
	border-radius: 0;
	justify-content: flex-start;

	${Eyebrow} {
		padding: ${theme.spacing.lg} ${theme.spacing.md};
		color: ${theme.colors.black};
	}
`;

const SsnPromptStep: FC<ISsnPromptStepProps> = ({ pageContent, stepKey }) => {
	const { pageHeader, pageDescription, yesAnswerLabel, noAnswerLabel } = pageContent;

	const { existingFormData, handleFormSubmission, handleNavBack, hideNavBack } =
		useContext(FlowContext);

	type Schema = Partial<z.input<typeof schema>>;

	const form = useForm<Schema>({
		resolver: zodResolver(schema),
		mode: 'onChange',
	});
	const {
		handleSubmit,
		control,
		watch,
		register,
		setValue,
		formState: { errors },
	} = form;

	const handleYes = () => {
		handleFormSubmission(
			{
				ssn:
					existingFormData?.[dataConstants.SSN] !== '000-00-0000'
						? existingFormData?.[dataConstants.SSN]
						: null,
			},
			stepKey
		);
	};

	const onSubmit = (data: Record<string, unknown>) => {
		handleFormSubmission(data, stepKey);
	};

	return (
		<SpacedGridItem marginBottom="lgxl">
			<FlexColumn gap="sm">
				<HeadingText>{pageHeader}</HeadingText>
				<WYSIWYGContent dangerouslySetInnerHTML={{ __html: pageDescription }} />
			</FlexColumn>
			<SpacedForm onSubmit={handleSubmit(onSubmit)}>
				<FlexColumn gap="lg">
					<RestyledButton
						label={yesAnswerLabel}
						onClick={handleYes}
						variant="secondary"
						dataCy="ssnYes"
					/>
					<input type="hidden" value="000-00-0000" {...register(dataConstants.SSN)} />
					<RestyledSubmit variant="secondary" label={noAnswerLabel} dataCy="ssnNo" />
				</FlexColumn>
			</SpacedForm>
		</SpacedGridItem>
	);
};

export default SsnPromptStep;
